import React, { useMemo, useState } from "react";
import { PageHeader, Result, FormulaBox, Note } from "@/components/CalcUI";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/i18n/LanguageProvider";
import { ALVARADO_CRITERIA } from "@/lib/score_data";

function interpret(score, t) {
  if (score <= 3) return { label: t("alv.interp.low"), tone: "success" };
  if (score <= 6) return { label: t("alv.interp.possible"), tone: "warning" };
  if (score <= 8) return { label: t("alv.interp.probable"), tone: "danger" };
  return { label: t("alv.interp.very"), tone: "danger" };
}

const GROUP_KEYS = {
  symptoms: "alv.group.symptoms",
  signs: "alv.group.signs",
  labs: "alv.group.labs",
};

export default function Alvarado() {
  const { t, lang } = useLanguage();
  const [checked, setChecked] = useState({});

  const score = useMemo(
    () => ALVARADO_CRITERIA.reduce((s, c) => s + (checked[c.id] ? c.points : 0), 0),
    [checked]
  );
  const interp = interpret(score, t);

  const groups = ["symptoms", "signs", "labs"];

  return (
    <div data-testid="page-alvarado">
      <PageHeader title={t("alv.title")} subtitle={t("alv.subtitle")} formula={t("alv.formula")} />

      <div className="px-6 md:px-10 py-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 max-w-6xl">
        <div className="space-y-6">
          {groups.map((g) => (
            <div
              key={g}
              className="border border-stone-200 bg-white rounded-sm"
              data-testid={`alvarado-group-${g}`}
            >
              <div className="px-5 pt-4 pb-2 text-[11px] tracking-[0.08em] uppercase font-semibold text-stone-500">
                {t(GROUP_KEYS[g])}
              </div>
              <div className="divide-y divide-stone-200">
                {ALVARADO_CRITERIA.filter((c) => c.group === g).map((c) => {
                  const on = !!checked[c.id];
                  const text = lang === "id" ? c.id_lang : c.en;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      data-testid={`alvarado-${c.id}`}
                      onClick={() => setChecked((prev) => ({ ...prev, [c.id]: !prev[c.id] }))}
                      className={cn(
                        "w-full flex items-center justify-between gap-4 px-5 py-3.5 text-left transition-colors duration-100",
                        on ? "bg-sky-700/5" : "bg-white hover:bg-stone-50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={cn(
                            "h-5 w-5 inline-flex items-center justify-center rounded-sm border shrink-0",
                            on ? "border-sky-700 bg-sky-700 text-white" : "border-stone-300 bg-white"
                          )}
                        >
                          {on && (
                            <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3">
                              <path
                                d="M3 8.5L6.5 12L13 4.5"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </span>
                        <span className="text-sm text-stone-800">{text}</span>
                      </div>
                      <span className="font-mono text-xs text-stone-500 shrink-0">+{c.points}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <Result
            label={t("alv.result.total")}
            value={score}
            unit="/ 10"
            big
            testid="result-alvarado"
            accent={interp.tone === "danger" ? "danger" : interp.tone === "success" ? "success" : "default"}
          />
          <Result
            label={t("alv.result.interpretation")}
            value={interp.label}
            testid="result-interpretation"
            accent={interp.tone === "danger" ? "danger" : interp.tone === "success" ? "success" : "default"}
          />
          <FormulaBox testid="alvarado-breakdown">{t("alv.legend")}</FormulaBox>
          <Note kind="warning">{t("alv.note")}</Note>
          <button
            type="button"
            data-testid="alvarado-reset"
            onClick={() => setChecked({})}
            className="w-full text-xs uppercase tracking-[0.08em] font-semibold border border-stone-200 bg-white hover:bg-stone-50 hover:border-stone-300 transition-colors duration-100 py-2.5 rounded-sm"
          >
            {t("common.reset")}
          </button>
        </div>
      </div>
    </div>
  );
}
